/**
 * Integrytas Group - Common Security Module (CSM)
 * TypeScript contract for authenticated sessions, RBAC and admin impersonation.
 *
 * Notes for downstream developers:
 * - All logic must key on `internal_code`. Never key off display strings.
 * - When `mimic.is_active === true`, every audit/forensic write MUST stamp
 *   `real_admin_id` (not `effective_user_id`) so the audit chain stays intact.
 * - Booleans here are UI booleans; DB still stores can_* as BOOLEAN and
 *   Y/N flags as CHAR(1). Conversion happens in the data-access layer.
 */

// -----------------------------------------------------------------------------
// Locale + display helpers
// -----------------------------------------------------------------------------

export type LocaleCode = string; // e.g. 'EN_US', 'ES_MX'

export interface TranslatedLabel {
  internal_code: string;        // immutable logic key
  display_text: string;         // resolved for current locale
  description?: string | null;
}

// -----------------------------------------------------------------------------
// User & RBAC primitives
// -----------------------------------------------------------------------------

export interface UserMaster {
  user_id: number;
  internal_code: string;
  email: string;
  display_name: string;
  preferred_locale: LocaleCode;
  is_admin: boolean;
  is_active: boolean;
}

export interface PageAccess {
  page_id: number;
  page_internal_code: string;   // e.g. 'CSM.USER.LIST'
  route_path: string;
  parent_page_id: number | null;  // FK to csm_page_master.page_id - drives menu hierarchy
  sort_order: number;             // sibling ordering within a parent
  can_view: boolean;
  can_add: boolean;
  can_change: boolean;
  can_delete: boolean;
  can_signoff: boolean;
  can_personalize_role: boolean;  // role-tier design enabled for this (role, page)
  can_personalize_user: boolean;  // user-tier personalization enabled for this (role, page)
  desktop_only: boolean;          // true = mobile app shows the Desktop-Only modal instead of routing (cascades to descendants)
}

export interface RoleSummary {
  role_id: number;
  internal_code: string;
  display_text: string;         // already localized
}

// -----------------------------------------------------------------------------
// Mimic (admin impersonation) state
// -----------------------------------------------------------------------------

export interface MimicState {
  is_active: boolean;
  user_mimic_log_id: number | null;
  real_admin_id: number | null;       // ALWAYS the real human admin
  mimicked_user_id: number | null;    // The user being impersonated
  started_at: string | null;          // ISO timestamp
  business_reason: string | null;
}

// -----------------------------------------------------------------------------
// Authenticated session contract
// -----------------------------------------------------------------------------

/**
 * The user whose permissions are *currently in effect* in the UI.
 * During a mimic session this is the impersonated user, NOT the admin.
 */
export interface UserContext {
  user_id: number;                     // Effective user id (may be the mimicked one)
  internal_code: string;
  display_name: string;
  email: string;
  preferred_locale: LocaleCode;
  /**
   * The EFFECTIVE user's admin flag — false while an admin mimics someone who
   * is not one. Use this for what the UI offers; it follows the same identity
   * as page_access, so a mimic session sees what that user would see.
   *
   * NOT an authorization decision. The server's csm_current_is_admin() resolves
   * the real signed-in identity from the JWT, which mimic does not swap, so an
   * admin keeps admin rights server-side throughout — deliberately: the real
   * human stays accountable (§12), and it is what lets them end the mimic,
   * since csm_user_mimic_log is admin-only.
   */
  is_admin: boolean;
  /**
   * Delegated capability flags (csm_user_master), grantable ONLY by a
   * CSM.SUPER_ADMIN. Each authorises the verb against users BENEATH this user
   * in the reporting line — never against the whole directory.
   *
   * Same caveat as `is_admin` above: these follow the EFFECTIVE user, so they
   * flip to the mimicked user's values mid-mimic. The server keeps resolving
   * the REAL signed-in identity, so they describe what the UI should offer,
   * not who may do what.
   */
  can_deactivate_users: boolean;
  can_mimic_users: boolean;
  /** This user's own place in the reporting line. Null at the top. */
  manager_user_id: number | null;
  roles: RoleSummary[];
  page_access: PageAccess[];           // Flattened RBAC matrix for fast lookup
  favorite_page_ids: number[];
}

/**
 * The full session envelope. `real_admin_id` is preserved at all times so
 * forensic writes never lose the true actor identity.
 */
export interface AuthSession {
  session_token: string;
  issued_at: string;                   // ISO timestamp
  expires_at: string;                  // ISO timestamp
  real_admin_id: number;               // Audit anchor - the human at the keyboard
  effective_user: UserContext;         // Permissions currently in force
  mimic: MimicState;
}

// -----------------------------------------------------------------------------
// Permission helpers (pure functions - safe in client and server)
// -----------------------------------------------------------------------------

export type AccessVerb = 'view' | 'add' | 'change' | 'delete' | 'signoff';

/** The role that may grant the delegated capability flags. */
export const SUPER_ADMIN_ROLE_CODE = 'CSM.SUPER_ADMIN';

/**
 * True when the effective user holds CSM.SUPER_ADMIN — the only role that may
 * set can_deactivate_users / can_mimic_users, and the only one exempt from the
 * reporting-line scope on both verbs.
 *
 * Keyed on internal_code, never on a display string (see the file header). The
 * server's own answer is csm_current_is_super_admin(); this is the UI's.
 */
export function isSuperAdmin(session: AuthSession): boolean {
  return session.effective_user.roles.some(r => r.internal_code === SUPER_ADMIN_ROLE_CODE);
}

export function hasAccess(
  session: AuthSession,
  pageInternalCode: string,
  verb: AccessVerb,
): boolean {
  const page = session.effective_user.page_access.find(
    (p) => p.page_internal_code === pageInternalCode,
  );
  if (!page) return false;
  switch (verb) {
    case 'view':    return page.can_view;
    case 'add':     return page.can_add;
    case 'change':  return page.can_change;
    case 'delete':  return page.can_delete;
    case 'signoff': return page.can_signoff;
  }
}

/**
 * Returns the user_id that must be stamped onto a forensic / audit column.
 * During a mimic session this is the real admin, never the impersonated user.
 */
export function getForensicActorId(session: AuthSession): number {
  return session.mimic.is_active ? session.real_admin_id : session.effective_user.user_id;
}
